import React, { useEffect, useState, useCallback } from 'react';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import axios from 'axios';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './calender.css';
import RightWrapper from '../../Components/CommonComponents/ComponentWrapper';
import { Button, Input, Modal } from '../../Components/UI';
import { FaCalendarAlt, FaPlus, FaTrashAlt, FaSync } from 'react-icons/fa';
import { END_POINT, getJwtToken } from '../../Redux/AdminReducer/action';

const localizer = momentLocalizer(moment);
const API_BASE_URL = `${END_POINT}/calendar`;

const Calendar = () => {
  const [events, setEvents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    start: new Date(),
    end: new Date(),
    allDay: false,
    description: ''
  });

  // FETCH EVENTS
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(API_BASE_URL, {
        headers: {
          Authorization: getJwtToken(),
        }
      });
      setEvents(
        (res.data || []).map((e) => ({
          ...e,
          start: new Date(e.start),
          end: new Date(e.end),
        }))
      );
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleSelectSlot = ({ start, end }) => {
    setSelectedEvent(null);
    setFormData({
      title: '',
      start,
      end: end || moment(start).add(1, 'hour').toDate(),
      allDay: false,
      description: ''
    });
    setError('');
    setShowModal(true);
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setFormData({
      title: event.title || '',
      start: new Date(event.start),
      end: new Date(event.end),
      allDay: Boolean(event.allDay),
      description: event.description || ''
    });
    setError('');
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }

    try {
      const payload = {
        ...formData,
        start: new Date(formData.start),
        end: new Date(formData.end)
      };

      if (selectedEvent) {
        await axios.put(`${API_BASE_URL}/${selectedEvent._id}`, payload, {
          headers: {
            Authorization: getJwtToken(),
          }
        });
      } else {
        await axios.post(API_BASE_URL, payload, {
          headers: {
            Authorization: getJwtToken(),
          }
        });
      }

      setShowModal(false);
      fetchEvents();
    } catch (err) {
      setError(err.response?.data?.message || 'Save failed');
    }
  };

  const handleDelete = async () => {
    if (!selectedEvent?._id) return;
    if (!window.confirm('Are you sure you want to delete this event?')) return;

    try {
      await axios.delete(`${API_BASE_URL}/${selectedEvent._id}`, {
        headers: {
          Authorization: getJwtToken(),
        }
      });
      setShowModal(false);
      fetchEvents();
    } catch (err) {
      setError(err.response?.data?.message || 'Delete failed');
    }
  };

  return (
    <RightWrapper>
      <div className="p-4 md:p-6 bg-slate-50 min-h-screen">
        {/* Top Header Card */}
        <div className="bg-white rounded-2xl shadow-xs border border-slate-200/80 p-4 md:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-xl bg-sky-100 text-sky-600 flex items-center justify-center text-lg">
                  <FaCalendarAlt />
                </div>
                Event Calendar
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Manage clinic events, doctor schedules, staff meetings, and key clinical milestones.
              </p>
            </div>

            <div className="flex items-center gap-2.5 self-start sm:self-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchEvents}
                disabled={loading}
                icon={<FaSync className={loading ? 'animate-spin' : ''} />}
              >
                Refresh
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  setSelectedEvent(null);
                  setFormData({
                    title: '',
                    start: new Date(),
                    end: moment().add(1, 'hour').toDate(),
                    allDay: false,
                    description: ''
                  });
                  setError('');
                  setShowModal(true);
                }}
                icon={<FaPlus />}
              >
                Add Event
              </Button>
            </div>
          </div>

          {error && (
            <div className="mt-4 p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm flex items-center justify-between">
              <span>{error}</span>
              <button
                type="button"
                onClick={() => setError('')}
                className="text-rose-500 hover:text-rose-700 font-bold ml-2"
              >
                &times;
              </button>
            </div>
          )}
        </div>

        {/* Calendar Main Card */}
        <div className="bg-white rounded-2xl shadow-xs border border-slate-200/80 p-4 md:p-6">
          <div className="w-full h-[650px] md:h-[750px]">
            <BigCalendar
              localizer={localizer}
              events={events}
              selectable
              startAccessor="start"
              endAccessor="end"
              style={{ height: '100%', width: '100%' }}
              onSelectSlot={handleSelectSlot}
              onSelectEvent={handleSelectEvent}
              eventPropGetter={() => ({
                className: 'transition-all hover:scale-[1.01]',
                style: {
                  background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                  borderRadius: '8px',
                  border: 'none',
                  color: '#ffffff',
                  fontWeight: '600',
                  boxShadow: '0 1px 3px rgba(2, 132, 199, 0.25)',
                }
              })}
            />
          </div>
        </div>

        {/* Add / Edit Event Modal */}
        <Modal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          title={selectedEvent ? 'Edit Event' : 'Add New Event'}
          subtitle={selectedEvent ? 'Update existing event details or remove it from the calendar.' : 'Schedule a new event on the clinic calendar.'}
          maxWidth="max-w-md"
          footer={
            <div className="flex items-center justify-between w-full">
              <div>
                {selectedEvent && (
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={handleDelete}
                    icon={<FaTrashAlt />}
                  >
                    Delete
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSubmit}
                >
                  {selectedEvent ? 'Update Event' : 'Save Event'}
                </Button>
              </div>
            </div>
          }
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Event Title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g. Clinical Staff Meeting"
              required
            />

            <div>
              <label className="block mb-1.5 text-xs font-semibold text-slate-700 tracking-wide">
                Start Date & Time <span className="text-rose-500">*</span>
              </label>
              <input
                type="datetime-local"
                name="start"
                value={moment(formData.start).format('YYYY-MM-DDTHH:mm')}
                onChange={(e) => setFormData({ ...formData, start: new Date(e.target.value) })}
                required
                className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 hover:border-slate-300 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 outline-none transition-all"
              />
            </div>

            <div>
              <label className="block mb-1.5 text-xs font-semibold text-slate-700 tracking-wide">
                End Date & Time <span className="text-rose-500">*</span>
              </label>
              <input
                type="datetime-local"
                name="end"
                value={moment(formData.end).format('YYYY-MM-DDTHH:mm')}
                onChange={(e) => setFormData({ ...formData, end: new Date(e.target.value) })}
                required
                className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 hover:border-slate-300 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 outline-none transition-all"
              />
            </div>

            <div className="flex items-center gap-2 pt-1">
              <input
                type="checkbox"
                id="allDayCheck"
                name="allDay"
                checked={formData.allDay}
                onChange={handleChange}
                className="w-4 h-4 text-sky-600 rounded border-slate-300 focus:ring-sky-500 cursor-pointer"
              />
              <label htmlFor="allDayCheck" className="text-xs font-semibold text-slate-700 cursor-pointer select-none">
                All Day Event
              </label>
            </div>

            <div>
              <label className="block mb-1.5 text-xs font-semibold text-slate-700 tracking-wide">
                Description / Notes
              </label>
              <textarea
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Add additional event details or notes..."
                className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 hover:border-slate-300 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 outline-none transition-all resize-none"
              />
            </div>
          </form>
        </Modal>
      </div>
    </RightWrapper>
  );
};

export default Calendar;
